import { StAddButtonWrapper, StAddFormField } from './styled/LCStyledComponents';
import { LCFormFieldValueList } from '../shared/LCFormFieldValueList';
import { THEME_PRIMARY, themeColor } from '../../../styles/basics/colors';
import { MATERIAL_ICON_CHECK } from '../../../shared/common/Input';
import { Button, ButtonSize } from '../../../shared/common/Button';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { InputWithSuggestions } from '../../../shared/common/InputWithSuggestions';
import { useKeywordSuggestions } from '../../../api/local-content/collection/product/useKeywordSuggestions';
import { useTranslation } from 'react-i18next';

const StInput = styled(InputWithSuggestions)`
    font-size: 15px;
`;

interface KeywordInputProps {
    keywords: string[];
    isEditable: boolean;
    onRemoveKeyword: (index: number) => void;
    onAddKeyword: (keyword: string, cb: () => void) => void;
}

export const KeywordInput = ({ onAddKeyword, onRemoveKeyword, isEditable, keywords }: KeywordInputProps) => {
    const { t } = useTranslation();
    const keywordInputRef = useRef<HTMLInputElement>(null);
    const [keywordInputMode, setKeywordInputMode] = useState(false);

    const [keyword, setKeyword] = useState<string>('');
    const { data: keywordSuggestions, isLoading: isKeywordsLoading } = useKeywordSuggestions(keyword);

    const handleClick = useCallback(
        event => {
            if (keywordInputRef.current) {
                const clickIsOutsideNode = !keywordInputRef?.current?.contains(event.target);
                if (clickIsOutsideNode) {
                    setKeywordInputMode(false);
                }
            }
        },
        [setKeywordInputMode]
    );

    useEffect(() => {
        document.addEventListener('mousedown', handleClick, false);

        if (keywordInputMode && keywordInputRef) {
            keywordInputRef.current?.focus();
        }
        return () => {
            document.removeEventListener('mousedown', handleClick, false);
        };
    }, [handleClick, keywordInputMode]);

    const handleAddKeyword = (keyword: string) => {
        onAddKeyword(keyword, () => {
            setKeywordInputMode(false);
            setKeyword('');
            keywordInputRef.current?.focus();
        });
    };
    return (
        <>
            <StAddFormField
                title={t('yourLocalContent.yourTitles.keywords')}
                tooltipText={t('yourLocalContent.yourTitles.tooltips.keywords')}
                isEdit={isEditable}
                editComponent={
                    <LCFormFieldValueList
                        value={keywords ?? []}
                        onRemove={index => onRemoveKeyword(index as number)}
                        editable={isEditable}
                    />
                }
            />
            {keywordInputMode ? (
                <StInput
                    ref={keywordInputRef}
                    iconColor={themeColor(THEME_PRIMARY)}
                    suffixIcon={MATERIAL_ICON_CHECK}
                    onEnterKey={handleAddKeyword}
                    onSuffixIconClick={handleAddKeyword}
                    onSuggestionClick={handleAddKeyword}
                    suggestions={keywordSuggestions ?? []}
                    loading={isKeywordsLoading ?? false}
                    onEscapeKey={() => setKeywordInputMode(false)}
                    excludedSuggestions={keywords}
                    onChange={value => setKeyword(value)}
                />
            ) : (
                <StAddButtonWrapper>
                    <Button
                        text={t('yourLocalContent.yourTitles.addKeyword')}
                        icon="add"
                        size={ButtonSize.SMALL}
                        onClick={() => setKeywordInputMode(true)}
                    />
                </StAddButtonWrapper>
            )}
        </>
    );
};
